import { useNavigate, useSearchParams } from '@solidjs/router'
import {
    api_admin_helmets_count_get,
    api_admin_helmets_list,
    api_admin_helmets_search_post,
    Count,
    Helmet,
    HelmetExtension,
} from 'abi'

import {
    addAlert,
    Counter,
    DatePicker,
    NextAndPrev,
    Pagination,
    SearchInp,
} from 'components'
import {
    CalendarIcon,
    CloseIcon,
    InsuranceIcon,
    PrintIcon,
    SortableIcon,
} from 'icons'
import { Component, createMemo, For, onCleanup, onMount, Show } from 'solid-js'
import { createStore, produce, unwrap } from 'solid-js/store'

import { AddBtn } from 'components/addBtn'
import { convertStrToNum, debounce, deepcopy, objentries } from 'utils/tools'
import { outletRef } from '..'
import { isAdmin } from '../users/corp/shared'
import { AddInsCmp } from './addIns'
import ExtentionReq from './extentionReq'
import PrintHelmet, { PrintHelmetExt } from './print'
import './style/insurances.scss'

// type company = string
export type SEARCH_FILTER =
    | Helmet['vin']
    | Helmet['chassis']
    | Helmet['engine']
    | Helmet['plate_number']
    | Helmet['plate_serial']
    | Helmet['insurance_number']
export type SEARCH_SORT = 'plate_number'

type SEARCH_BTN = {
    label: string
}

type SEARCH_FILTERS = Record<SEARCH_FILTER, SEARCH_BTN>
export const searchFilters: SEARCH_FILTERS = {
    vin: {
        label: 'vin',
    },
    chassis: {
        label: 'شماره شاسي',
    },
    engine: {
        label: 'شماره موتور',
    },
    plate_number: {
        label: 'شماره پلاک',
    },
    plate_serial: {
        label: 'سریال پلاک',
    },
    insurance_number: {
        label: 'شماره بیمه',
    },
    // company: {
    //     label: 'شرکت',
    // },
}

type STATE = {
    count: Count

    page: number

    filter: SEARCH_FILTER | null
    sort: SEARCH_SORT | null

    datePicker: {
        show: boolean
        date: string | null
    }

    query: string

    add: {
        type: 'edit' | 'add'
        show: boolean
        ins: Helmet | null
    }

    extention: Helmet | null

    print: Helmet | null
    extenttionPrint: HelmetExtension | null

    loadingC: boolean
    loadingI: boolean

    ins: Helmet[]
}
const [state, setState] = createStore<STATE>({
    loadingI: true,
    loadingC: true,

    filter: null,
    sort: null,
    query: '',
    ins: [],

    print: null,

    datePicker: {
        show: false,
        date: null,
    },

    extention: null,
    extenttionPrint: null,

    page: 0,
    count: {
        max_page: -1,
        total: -1,
    },

    add: {
        show: false,
        type: 'add',
        ins: null,
    },
})

export { state as InsS }

const Insurances: Component = () => {
    let nav = useNavigate()

    const [search_params, setSearchParams] = useSearchParams()

    onMount(() => {
        if (!isAdmin()) return nav('/panel/403', { replace: true })

        let p = parseInt(search_params.page || '0')
        if (p !== 0) {
            setState('page', p)
        }

        let page = unwrap(state.page)
        setSearchParams({ page })

        let ac = new AbortController()

        getCount()
        searchIns()

        window.addEventListener('popstate', handlePopstate, {
            signal: ac.signal,
        })

        onCleanup(() => ac.abort())
    })

    const handlePopstate = () => {
        let page = parseInt(search_params.page || '0')

        doSearch(page)
    }

    const getCount = async () => {
        setState({ loadingC: true })
        let res = await api_admin_helmets_count_get({})

        if (res.ok()) {
            setState({ count: res.body })
        }
        setState({ loadingC: false })
    }

    const doSearch = (page: number) => {
        setSearchParams({ page })
        setState({ page })
        searchIns()

        outletRef?.scrollTo({
            top: 0,
            behavior: 'smooth',
        })
    }

    const isSearching = createMemo(() => {
        let s = state
        let d = state.datePicker.date

        return s.sort || s.filter || d || s.query.length !== 0
    })

    const searchIns = async (goToStart: boolean = false) => {
        let s = deepcopy(state)
        let d = s.datePicker.date
        const searching = unwrap(isSearching())

        let page = s.page

        let result: Helmet[] = []

        setState('loadingI', true)

        if (searching) {
            if (!state.filter && !d && !s.sort) {
                return addAlert({
                    type: 'info',
                    timeout: 3,
                    subject: 'فیلتر جستجو خود را انتخاب کنید.',
                    content: 'مثال: شماره‌ شاسی، شماره‌ پلاک، ...',
                })
            }

            if (goToStart) {
                page = 0
                setSearchParams({ page: 0 })
            }

            let f = unwrap(state.filter)

            let res = await api_admin_helmets_search_post(
                {
                    page,
                    company: null,
                    created_at_from: null,
                    created_at_to: null,
                    chassis: f == 'chassis' ? `%${s.query}%` : null,
                    engine: f == 'engine' ? `%${s.query}%` : null,
                    plate_number: f == 'plate_number' ? `%${s.query}%` : null,
                    plate_serial: f == 'plate_serial' ? `%${s.query}%` : null,
                    vin: f == 'vin' ? `%${s.query}%` : null,
                    issue_date: d,
                    insurance_number:
                        f == 'insurance_number'
                            ? parseInt(convertStrToNum(s.query))
                            : null,
                    order: 'asc',
                    sort: s.sort,
                },
                { show_alert: false }
            )

            if (!res.ok()) return setState('loadingI', false)

            result = res.body
        } else {
            let res = await api_admin_helmets_list({
                page,
            })

            if (!res.ok()) return setState('loadingI', false)

            result = res.body
        }

        setState({ loadingI: false, ins: result, page })
    }

    const setFilter = (k: SEARCH_FILTER) => {
        if (state.filter == k) setState('filter', null)
        else setState('filter', k)

        searchIns(true)
    }

    const setSort = (k: SEARCH_SORT) => {
        if (!k) return setState('sort', null)

        if (state.sort == k) setState('sort', null)
        else setState('sort', k)

        searchIns()
    }

    const printedHelmet = async (_: Helmet) => {
        // not for admin
        return
    }

    const debouncedSetSearch = debounce(() => {
        searchIns(true)
    }, 200)

    return (
        <div class='insurances-section section'>
            <div class='insurances-wrapper'>
                <header class='head section_title'>
                    <InsuranceIcon />
                    <span>بیمه ها</span>
                    <InsuranceIcon />
                </header>

                <div
                    class='total-wrapper title_small'
                    classList={{ active: !state.loadingC }}
                >
                    <Counter end={state.count.total} format />
                    <span>بیمه ثبت شده</span>
                </div>

                <AddBtn
                    onClick={() => {
                        setState('add', { show: true, ins: null, type: 'add' })
                    }}
                    class='title_smaller'
                    title='اضافه کردن'
                />

                <form
                    class='filters-container'
                    onsubmit={e => {
                        e.preventDefault()

                        searchIns(true)
                    }}
                >
                    <SearchInp
                        value={state.query}
                        active={
                            state.query.length !== 0 ||
                            !!state.filter ||
                            !!state.datePicker.date
                        }
                        oninput={e => {
                            setState({ query: e.currentTarget.value.trim() })

                            debouncedSetSearch()
                        }}
                        onClear={() => {
                            setState({ query: '' })
                            searchIns(true)
                        }}
                        id='insurances-search-inp'
                    />
                    <div class='search-hints title_smaller'>
                        <div class='search-hint'>
                            <For each={objentries(searchFilters).slice(0, 3)}>
                                {([k, v]) => (
                                    <>
                                        <button
                                            class='hint-btn'
                                            type='button'
                                            classList={{
                                                active: state.filter == k,
                                            }}
                                            onclick={() => setFilter(k)}
                                        >
                                            {v.label}
                                        </button>
                                        <div class='divider'>|</div>
                                    </>
                                )}
                            </For>
                        </div>
                        <div class='search-hint'>
                            <For each={objentries(searchFilters).slice(3, 6)}>
                                {([k, v]) => (
                                    <>
                                        <button
                                            class='hint-btn'
                                            type='submit'
                                            classList={{
                                                active: state.filter == k,
                                            }}
                                            onclick={() => setFilter(k)}
                                        >
                                            {v.label}
                                        </button>
                                        <div class='divider'>|</div>
                                    </>
                                )}
                            </For>
                        </div>
                    </div>

                    <div
                        class='date-picker-container'
                        classList={{ active: !!state.datePicker.date }}
                    >
                        <button
                            type='button'
                            onclick={() => setState('datePicker', 'show', true)}
                            class='date-picker-wrapper title_smaller'
                        >
                            <CalendarIcon />
                            فیلتر با تاریخ صدور
                            <CalendarIcon />
                        </button>
                        <Show when={state.datePicker.date}>
                            <button
                                type='button'
                                onclick={() =>
                                    setState('datePicker', 'show', true)
                                }
                                class='clear-date title_smaller'
                            >
                                {state.datePicker.date}
                            </button>
                        </Show>
                    </div>
                </form>

                <div
                    class='table-container'
                    classList={{
                        min: state.ins?.length == 0,
                        loading: state.loadingI,
                    }}
                >
                    <table>
                        <thead>
                            <tr class='title_small'>
                                <th class='index'>
                                    <span>ردیف</span>
                                </th>
                                <th>
                                    <span>VIN</span>
                                </th>
                                <th>
                                    <span>شاسی</span>
                                </th>
                                <th>
                                    <span>موتور</span>
                                </th>
                                <th>
                                    <span>سریال پلاک</span>
                                </th>
                                <th
                                    class='sortable'
                                    classList={{
                                        active: state.sort == 'plate_number',
                                    }}
                                    onclick={() => setSort('plate_number')}
                                >
                                    <span>
                                        <SortableIcon />
                                        شماره پلاک
                                    </span>
                                </th>
                                <th>
                                    <span>رنگ</span>
                                </th>
                                <th>
                                    <span>تاریخ صدور</span>
                                </th>
                                <th>
                                    <span>چاپ</span>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <Show when={!state.loadingI} fallback={<Loading />}>
                                <Show
                                    when={state.ins.length > 0}
                                    fallback={
                                        <tr class='table-empty title_hero'>
                                            <td colspan='10'>
                                                <div class='icon'>
                                                    <CloseIcon />
                                                </div>
                                                <div>
                                                    بیمه ای برای نمایش وجود
                                                    ندارد!
                                                </div>
                                            </td>
                                        </tr>
                                    }
                                >
                                    <For each={state.ins}>
                                        {c => (
                                            <HelmetTableRow
                                                ins={c}
                                                print={() => {
                                                    console.log(c.id)
                                                    setState('print', c)
                                                }}
                                                onClick={() => {
                                                    setState('add', {
                                                        ins: c,
                                                        show: true,
                                                        type: 'edit',
                                                    })
                                                }}
                                            />
                                        )}
                                    </For>
                                </Show>
                            </Show>
                        </tbody>
                    </table>
                </div>

                <Show when={state.count.max_page > 0}>
                    <Show
                        when={!isSearching()}
                        fallback={
                            <NextAndPrev
                                max_page={state.count.max_page}
                                current={state.page || 0}
                                onChange={page => {
                                    doSearch(page)
                                }}
                            />
                        }
                    >
                        <Pagination
                            max_page={state.count.max_page}
                            current={state.page || 0}
                            onChange={page => {
                                doSearch(page)
                            }}
                        />
                    </Show>
                </Show>
            </div>

            <AddInsCmp
                onClose={() =>
                    setState('add', { type: 'add', ins: null, show: false })
                }
                add={state.add}
                onDel={c => {
                    setState(
                        produce(s => {
                            let id = s.ins.findIndex(a => a.id == c.id)
                            if (id == -1) return

                            s.ins.splice(id, 1)
                        })
                    )
                }}
                onExtention={c => setState('extention', c)}
                onAdd={c => {
                    {
                        setState(
                            produce(s => {
                                s.ins.push(...c)
                                s.count.total += c.length
                            })
                        )
                    }
                }}
                onEdit={c => {
                    {
                        setState(
                            produce(s => {
                                let id = s.ins.findIndex(a => a.id == c.id)
                                if (id == -1) return

                                s.ins[id] = deepcopy(c)
                            })
                        )
                    }
                }}
                onPrint={c => {
                    setState(
                        produce(s => {
                            // s.add = {
                            //     ins: null,
                            //     show: false,
                            //     type: 'add',
                            // }
                            s.print = c
                        })
                    )
                }}
                onExtentionPrint={c => {
                    setState(
                        produce(s => {
                            s.extenttionPrint = c
                        })
                    )
                }}
            />

            <DatePicker
                show={state.datePicker.show}
                current={state.datePicker.date || ''}
                onClose={() => {
                    setState('datePicker', 'show', false)
                }}
                onSave={d => {
                    setState('datePicker', {
                        show: false,
                        date: d,
                    })
                    searchIns(true)
                }}
                onClear={() => {
                    setState('datePicker', {
                        show: false,
                        date: null,
                    })
                    searchIns(true)
                }}
            />

            <Show when={state.extention}>
                <ExtentionReq
                    helmet={state.extention!}
                    onClose={() => setState('extention', null)}
                    onSave={() => {
                        addAlert({
                            type: 'success',
                            subject: 'درخواست شما ثبت شد!',
                            timeout: 3,
                            content:
                                'در زمان کمی به درخواست شما پاسخ داده خواهد شد.',
                        })
                        setState('extention', null)
                    }}
                />
            </Show>

            <Show when={state.print}>
                <PrintHelmet
                    onExtention={() =>
                        setState('extention', unwrap(state.print))
                    }
                    onPrint={() => printedHelmet(state.print!)}
                    helmet={state.print!}
                    onClose={() => setState('print', null)}
                    onExtentionPrint={c => {
                        setState('extenttionPrint', c)
                    }}
                />
            </Show>
            <Show when={state.extenttionPrint}>
                <PrintHelmetExt
                    onPrint={() => {}}
                    helmetExt={state.extenttionPrint!}
                    onClose={() => setState('extenttionPrint', null)}
                />
            </Show>
        </div>
    )
}

type TableRowProps = {
    ins: Helmet
    onClick(): void
    print(): void
}
export const HelmetTableRow: Component<TableRowProps> = P => {
    return (
        <tr class='title_smaller' onclick={P.onClick}>
            <td class='index'>{P.ins.id}</td>
            <td class='vin'>{P.ins.vin || <span class='empty'>خالی!</span>}</td>
            <td>{P.ins.chassis || <span class='empty'>خالی!</span>}</td>
            <td>{P.ins.engine || <span class='empty'>خالی!</span>}</td>
            <td>{P.ins.plate_serial || <span class='empty'>خالی!</span>}</td>
            <td>{P.ins.plate_number || <span class='empty'>خالی!</span>}</td>
            <td>{P.ins.v_color || <span class='empty'>خالی!</span>}</td>
            <td class='date'>{P.ins.issue_date}</td>
            <td>
                <Show when={P.ins.provider == 'day'}>
                    <a
                        onclick={e => e.stopPropagation()}
                        class='print-icon icon'
                        target='_blank'
                        href={`https://fan2.dayins.com/BimeSiteMVC/Car/SalesBnPrint/GetSalesBnPrint?fullBNo=${P.ins.issue_year}%2F${'31'}%2F${P.ins.issuer_code}%2F${P.ins.insurance_number}&bimeGozarId=${P.ins.insurer_code}&reshte=5`}
                    >
                        <PrintIcon />
                    </a>
                </Show>
                <Show when={P.ins.provider == 'tejaratno'}>
                    <button
                        class='print-icon icon'
                        style={{ width: '100%' }}
                        onClick={e => {
                            e.stopPropagation()
                            P.print()
                        }}
                    >
                        <PrintIcon />
                    </button>
                </Show>
            </td>
        </tr>
    )
}

const Loading = () => {
    return (
        <tr class='table-loading title_hero'>
            <td colspan='10'>
                <img src='/public/imgs/logo.png' decoding='async' alt='' />
                <div>در حال بارگذاری...</div>
            </td>
        </tr>
    )
}

export { type STATE as INSURANCE_STATE }

export default Insurances
